import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { parseMoneyToCents } from '../lib/format';
import { slugify } from '../lib/slug';
import { CATEGORY_BANK, categoryName } from '../lib/categories';

interface Cat { tempId: string; key: string }
const catLabel = (key: string, locale: string) => {
  const d = CATEGORY_BANK.find((x) => x.key === key);
  return d ? (d[locale as keyof typeof d] ?? d['pt-BR']) : key;
};
interface ItemDraft { tempId: string; catTempId: string; name: string; description: string; price: string; station: string }

function uniqueSlug(input: string): string {
  const base = slugify(input) || 'restaurante';
  return `${base}-${Math.random().toString(36).slice(2, 6)}`;
}

const CURRENCIES = ['BRL', 'USD', 'EUR'];
const TIMEZONES = ['America/Sao_Paulo', 'America/New_York', 'Europe/Lisbon', 'Europe/Paris', 'Europe/Madrid'];
const LANGS = [
  { code: 'pt-BR', label: 'PT' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' }
];

export function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // passo 1 — base
  const [locale, setLocale] = useState(i18n.resolvedLanguage === 'pt' ? 'pt-BR' : (i18n.resolvedLanguage ?? 'pt-BR'));
  const [name, setName] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [country, setCountry] = useState('Brasil');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [currency, setCurrency] = useState('BRL');
  const [timezone, setTimezone] = useState('America/Sao_Paulo');

  // passo 2 — recebimento
  const [pixKey, setPixKey] = useState('');
  const [pixName, setPixName] = useState('');
  const [pixCity, setPixCity] = useState('');
  const [iban, setIban] = useState('');
  const [ibanName, setIbanName] = useState('');

  // passo 3 — cardápio
  const [cats, setCats] = useState<Cat[]>([]);
  const [catPick, setCatPick] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([]);

  // passo 4 — mesas
  const [tableCount, setTableCount] = useState('6');

  function pickLang(code: string) {
    setLocale(code);
    void i18n.changeLanguage(code);
  }

  const i18nText = (s: string) => ({ [locale]: s });

  function addCat() {
    if (!catPick || cats.some((c) => c.key === catPick)) return;
    setCats((p) => [...p, { tempId: crypto.randomUUID(), key: catPick }]);
    setCatPick('');
  }
  function addItem() { setItems((p) => [...p, { tempId: crypto.randomUUID(), catTempId: cats[0]?.tempId ?? '', name: '', description: '', price: '', station: 'cozinha' }]); }

  // itens incompletos travam o avanço do passo de cardápio
  const itemsValid = items.every((it) => it.name.trim() && it.price.trim() && it.description.trim() && it.catTempId);
  const canNextBase = Boolean(name.trim() && cuisine.trim() && country.trim() && state.trim() && city.trim());

  async function finish() {
    setBusy(true);
    setError(null);
    try {
      const { data: rid, error: rErr } = await supabase.rpc('create_restaurant_with_owner', {
        p_name: name, p_slug: uniqueSlug(name), p_locale: locale, p_currency: currency,
        p_cuisine: cuisine || null, p_region: city || null, p_timezone: timezone
      });
      if (rErr) throw rErr;
      const restaurantId = rid as string;

      // geografia (para a página pública / diretório) + região
      await supabase.from('restaurants').update({
        country, state, city,
        country_slug: slugify(country), state_slug: slugify(state), city_slug: slugify(city)
      }).eq('id', restaurantId);

      if (pixKey || iban) {
        await supabase.from('restaurant_payment').insert({
          restaurant_id: restaurantId,
          pix_key: pixKey || null, pix_merchant_name: pixName || null, pix_city: pixCity || null,
          sepa_iban: iban || null, sepa_name: ibanName || null
        });
      }

      const idByTemp: Record<string, string> = {};
      for (let i = 0; i < cats.length; i++) {
        const c = cats[i];
        const def = CATEGORY_BANK.find((d) => d.key === c.key);
        if (!def) continue;
        const { data: cat } = await supabase.from('menu_categories')
          .insert({ restaurant_id: restaurantId, name: categoryName(def), position: i + 1 })
          .select('id').single();
        if (cat) idByTemp[c.tempId] = cat.id as string;
      }
      const itemRows = items
        .filter((it) => it.name.trim() && it.price.trim() && it.description.trim())
        .map((it, i) => ({
          restaurant_id: restaurantId,
          category_id: idByTemp[it.catTempId] ?? null,
          name: i18nText(it.name.trim()),
          description: i18nText(it.description.trim()),
          price_cents: parseMoneyToCents(it.price),
          station: it.station,
          position: i + 1
        }));
      if (itemRows.length) await supabase.from('menu_items').insert(itemRows);

      const n = Math.max(0, Math.min(200, Number(tableCount) || 0));
      if (n > 0) {
        await supabase.from('tables').insert(
          Array.from({ length: n }, (_, i) => ({ restaurant_id: restaurantId, label: String(i + 1).padStart(2, '0'), seats: 2 }))
        );
      }

      navigate('/app', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar o restaurante.');
      setStep(0);
    } finally {
      setBusy(false);
    }
  }

  const STEPS = ['step_base', 'step_payment', 'step_menu', 'step_tables'] as const;

  return (
    <div className="auth-card auth-card--wide">
      <h1 className="auth-card__title">{t('onboarding.title')}</h1>
      <p className="auth-card__subtitle">{t('onboarding.subtitle')}</p>

      <ol className="wizard-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`wizard-steps__item${i === step ? ' is-active' : ''}${i < step ? ' is-done' : ''}`}>
            <span className="wizard-steps__num">{i + 1}</span>{t(`onboarding.${s}`)}
          </li>
        ))}
      </ol>

      {/* PASSO 1 — base */}
      {step === 0 && (
        <div className="form">
          <div className="field">
            <span>{t('onboarding.locale_label')}</span>
            <div className="lang-seg">
              {LANGS.map((l) => (
                <button key={l.code} type="button" className={`lang-seg__btn${locale === l.code ? ' is-on' : ''}`} onClick={() => pickLang(l.code)}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <label className="field"><span>{t('onboarding.name_label')}</span><input required value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="field"><span>{t('onboarding.cuisine_label')}</span><input required value={cuisine} onChange={(e) => setCuisine(e.target.value)} /></label>
          <div className="form-row">
            <label className="field"><span>{t('onboarding.country')}</span><input required value={country} onChange={(e) => setCountry(e.target.value)} /></label>
            <label className="field"><span>{t('onboarding.state')}</span><input required value={state} onChange={(e) => setState(e.target.value)} /></label>
            <label className="field"><span>{t('onboarding.city')}</span><input required value={city} onChange={(e) => setCity(e.target.value)} /></label>
          </div>
          <div className="form-row">
            <label className="field"><span>{t('onboarding.currency_label')}</span>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </label>
            <label className="field"><span>{t('onboarding.timezone_label')}</span>
              <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>{TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}</select>
            </label>
          </div>
        </div>
      )}

      {/* PASSO 2 — recebimento */}
      {step === 1 && (
        <div className="form">
          <p className="muted-note">{t('onboarding.payment_intro')}</p>
          {currency === 'EUR' ? (
            <>
              <label className="field"><span>{t('pay.sepa_iban')}</span><input value={iban} onChange={(e) => setIban(e.target.value)} /></label>
              <label className="field"><span>{t('pay.sepa_name')}</span><input value={ibanName} onChange={(e) => setIbanName(e.target.value)} /></label>
            </>
          ) : (
            <>
              <label className="field"><span>{t('pay.pix_key')}</span><input value={pixKey} onChange={(e) => setPixKey(e.target.value)} /></label>
              <div className="form-row">
                <label className="field"><span>{t('pay.pix_name')}</span><input value={pixName} onChange={(e) => setPixName(e.target.value)} /></label>
                <label className="field"><span>{t('pay.pix_city')}</span><input value={pixCity} onChange={(e) => setPixCity(e.target.value)} /></label>
              </div>
            </>
          )}
        </div>
      )}

      {/* PASSO 3 — cardápio */}
      {step === 2 && (
        <div className="form">
          <p className="muted-note">{t('onboarding.menu_intro')}</p>

          <span className="onb-sec">{t('menu.categories')}</span>
          {cats.length > 0 && (
            <div className="day-toggles">
              {cats.map((c) => (
                <span key={c.tempId} className="day-chip day-chip--on">
                  {catLabel(c.key, locale)}
                  <button type="button" className="link-button" style={{ padding: '0 0 0 8px', color: '#fff' }} onClick={() => setCats((p) => p.filter((x) => x.tempId !== c.tempId))}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="form-row">
            <select className="onb-input" value={catPick} onChange={(e) => setCatPick(e.target.value)}>
              <option value="">{t('menu.pick_category')}</option>
              {CATEGORY_BANK.filter((d) => !cats.some((c) => c.key === d.key)).map((d) => (
                <option key={d.key} value={d.key}>{d[locale as keyof typeof d] ?? d['pt-BR']}</option>
              ))}
            </select>
            <button type="button" className="btn btn--ghost btn--sm" disabled={!catPick} onClick={addCat}>{t('onboarding.add_category')}</button>
          </div>

          {cats.length > 0 && (
            <>
              <span className="onb-sec">{t('menu.items')}</span>
              {items.map((it, ii) => (
                <div key={it.tempId} className="onb-item">
                  <input className="onb-input" placeholder={t('onboarding.item_name')} value={it.name}
                    onChange={(e) => setItems((p) => p.map((x, i) => (i === ii ? { ...x, name: e.target.value } : x)))} />
                  <input className="onb-input" placeholder={t('onboarding.item_desc')} value={it.description}
                    onChange={(e) => setItems((p) => p.map((x, i) => (i === ii ? { ...x, description: e.target.value } : x)))} />
                  <div className="form-row">
                    <input className="onb-input" placeholder={`${t('onboarding.item_price')} (${currency})`} inputMode="decimal" value={it.price}
                      onChange={(e) => setItems((p) => p.map((x, i) => (i === ii ? { ...x, price: e.target.value.replace(/[^0-9.,]/g, '') } : x)))} />
                    <select className="onb-input" value={it.catTempId} onChange={(e) => setItems((p) => p.map((x, i) => (i === ii ? { ...x, catTempId: e.target.value } : x)))}>
                      {cats.map((c) => <option key={c.tempId} value={c.tempId}>{catLabel(c.key, locale)}</option>)}
                    </select>
                    <select className="onb-input" value={it.station} onChange={(e) => setItems((p) => p.map((x, i) => (i === ii ? { ...x, station: e.target.value } : x)))}>
                      <option value="cozinha">{t('menu.station_kitchen')}</option>
                      <option value="bar">{t('menu.station_bar')}</option>
                    </select>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn--ghost btn--sm" onClick={addItem} style={{ alignSelf: 'flex-start' }}>{t('onboarding.add_item')}</button>
              {items.length > 0 && !itemsValid && <p className="muted-note">{t('onboarding.menu_required')}</p>}
            </>
          )}
        </div>
      )}

      {/* PASSO 4 — mesas */}
      {step === 3 && (
        <div className="form">
          <p className="muted-note">{t('onboarding.tables_intro')}</p>
          <label className="field"><span>{t('onboarding.tables_count')}</span>
            <input type="number" min={0} max={200} value={tableCount} onChange={(e) => setTableCount(e.target.value)} />
          </label>
          <span className="muted-note">{t('onboarding.tables_hint')}</span>
        </div>
      )}

      {error && <p className="form-error">{error}</p>}

      <div className="form-row" style={{ marginTop: 16 }}>
        {step > 0 && <button type="button" className="btn btn--ghost" onClick={() => setStep((s) => s - 1)} disabled={busy}>{t('onboarding.back')}</button>}
        {step < 3 ? (
          <>
            <button type="button" className="btn btn--primary"
              disabled={(step === 0 && !canNextBase) || (step === 2 && !itemsValid)}
              onClick={() => setStep((s) => s + 1)}>
              {t('onboarding.next')}
            </button>
            {step >= 1 && !(step === 2 && items.length > 0) && <button type="button" className="link-button" onClick={() => setStep((s) => s + 1)}>{t('onboarding.skip')}</button>}
          </>
        ) : (
          <button type="button" className="btn btn--primary" onClick={() => void finish()} disabled={busy || !canNextBase}>
            {busy ? t('onboarding.creating') : t('onboarding.finish')}
          </button>
        )}
      </div>
    </div>
  );
}
